import { useEffect, useState, useRef, type FunctionComponent, type ReactNode } from "react";
import { TipoActividad } from "@/shared/types/enums/TipoActividadNotificaciones.enum";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { useDisableTwoFA, useEnableTwoFA } from "@/shared/hooks/useConfiguracion.hook";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import customAlert from "../../CustomAlert";
import type { EnableTwoFAResponse } from "@/shared/types/interfaces/GestionCasosApi/Authentication.interface";
import { useConfirmation } from "@/shared/hooks/useConfirmation.hook";
import QRCodeScanner from "./QRCodeScanner";
import ItemConfig from "../Notificaciones/ItemConfig";

interface TwoFactorAuthenticatorProps {
    onClose: () => void
    isActiveTwoFA: boolean
    onRegisterFooter?: (node: ReactNode) => void
}

const TwoFactorAuthenticator: FunctionComponent<TwoFactorAuthenticatorProps> = ({
    isActiveTwoFA, onClose, onRegisterFooter
}) => {

    const { t } = useTranslation();

    const { control, handleSubmit, watch, reset, setValue } = useForm<{ TwoFA: boolean }>({
        defaultValues: {
            TwoFA: isActiveTwoFA
        },
    });

    const {
        confirmated: openQrCodeScanner,
        confirmation: isOpenQrCodeScanner,
        notConfirmated: closeQrCodeScanner
    } = useConfirmation({initialValue: false})

    const [enableTwoFADataResponse, setEnableTwoFADataResponse] = useState<EnableTwoFAResponse | null>(null);

    const {
        mutate: mutateEnableTwoFA,
        isPending: isPendingEnableTwoFA
    } = useEnableTwoFA();
    
    const {
        mutate: mutateDisableTwoFA,
        isPending: isPendingDisableTwoFA
    } = useDisableTwoFA();

    useEffect(() => {
        setValue("TwoFA", isActiveTwoFA);
    }, [isActiveTwoFA, setValue]);

    const handleSaveTwoFA : SubmitHandler<{ TwoFA: boolean }> = (data) => {
        console.log(data)

        if(data.TwoFA === isActiveTwoFA){
            customAlert({
                icon: 'info',
                titulo: `${t('configuracionPerfil.noChangesToSave')}`,
                mensaje: `${t('configuracionPerfil.sameConfigurationNoChangesSaved')}`,
                textoBotonAceptar: `${t('actions.accept')}`
            })
            return
        }

        customAlert({
            icon: "question",
            titulo: `${!isActiveTwoFA ? `${t('configuracionPerfil.confirmActivate2FA')}` : `${t('configuracionPerfil.confirmDisable2FA')}`}`,
            mensaje: `${!isActiveTwoFA ? `${t('configuracionPerfil.securityImprovementPinRequired')}` : `${t('configuracionPerfil.doNotRecommendDisabling2FA')}`}`,
            botonCancelar: true,
            cerrarEsc: false,
            cerrarClicFuera: false,
            textoBotonAceptar: "Sí",
            colorBotonCancelar: '#d33',
            textoBotonCancelar: "No",
            callback: (res) =>  {
                if (res) {
                    if(isActiveTwoFA){
                        console.log('desactivar')
                        mutateDisableTwoFA({},
                            {
                                onSuccess: (res) => {
                                    console.log(res)
                                    //reset();
                                    customAlert({
                                        icon: 'success',
                                        titulo: `${t('configuracionPerfil.disabled2FASuccessfully')}`,
                                        tiempo: 4,
                                        toast: true,
                                    });
                                    onClose();
                                },
                                onError: (err) => {
                                    customAlert({
                                        icon: 'error',
                                        titulo: err.message || `${t('configuracionPerfil.errorDisabling2FA')}`,
                                        tiempo: 8,
                                        toast: true,
                                    });
                                }
                            }
                        );
                        return
                    }
                    console.log('activar')
                    mutateEnableTwoFA({}, 
                        {
                            onSuccess: (res) => {
                                console.log(res)
                                setEnableTwoFADataResponse(res);
                                openQrCodeScanner();
                                //onClose();
                                //reset();
                                //customAlert({
                                    //icon: 'success',
                                    //titulo: `Configuración de notificaciones actualizada con exito!`,
                                    //tiempo: 4,
                                    //toast: true,
                                //});
                            },
                            onError: (err) => {
                                customAlert({
                                    icon: 'error',
                                    titulo: err.message || `${t('configuracionPerfil.errorActivating2FA')}`,
                                    tiempo: 8,
                                    toast: true,
                                });
                            }
                        }
                    );
                }          
            }
        });
    }

    const isPending = isPendingEnableTwoFA || isPendingDisableTwoFA;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const handleSubmitRef = useRef(handleSubmit);
    handleSubmitRef.current = handleSubmit;
    const handleSaveTwoFARef = useRef(handleSaveTwoFA);
    handleSaveTwoFARef.current = handleSaveTwoFA;

    useEffect(() => {
        if (!onRegisterFooter) return;
        onRegisterFooter(
            <>
                <Button
                    variant="outlined"
                    color='error'
                    onClick={() => onCloseRef.current()}
                    disabled={isPending}
                >
                    {t('configuracionPerfil.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => handleSubmitRef.current(handleSaveTwoFARef.current)()}
                    className="normal-case rounded-xl px-6 shadow-md"
                    disabled={isPending}
                    loading={isPending}
                    loadingPosition="end"
                >
                    {t('configuracionPerfil.saveChanges')}
                </Button>
            </>
        );
        return () => { onRegisterFooter(null); };
    }, [isPending, onRegisterFooter, t]);

    return (
        <>
        <section className="flex flex-col h-full w-full">
            <Controller
                name={`TwoFA`}
                control={control}
                disabled={isPendingEnableTwoFA || isPendingDisableTwoFA}
                render={({ field }) => (
                    <ItemConfig
                        TipoActividad={TipoActividad.TwoFactorAuthenticator}
                        title={
                            !isActiveTwoFA 
                            ? `${t('configuracionPerfil.twoFactorAuthenticator')}`
                            : `${t('configuracionPerfil.twoFactorAuthenticator')}`
                        }
                        desc={
                            !isActiveTwoFA 
                            ?  `${t('configuracionPerfil.securityImprovementTokenRequired')}`
                            : `${t('configuracionPerfil.doNotRecommendDisabling2FA')}`
                        }
                        hardColor={'bg-sky-500'}
                        lightColor={'bg-sky-100'}
                        habilitado={field.value}
                        onChange={field.onChange}
                        width="w-full"
                    />
                )}
            />

        </section>
        {
            isOpenQrCodeScanner && enableTwoFADataResponse && (
                <QRCodeScanner 
                    showModal={isOpenQrCodeScanner}
                    closeModal={closeQrCodeScanner}
                    QrCodeUrl={enableTwoFADataResponse.QrCodeUrl}
                    BackupCodes={enableTwoFADataResponse.BackupCodes}
                />
            )
        }
        </>
    );
}

export default TwoFactorAuthenticator;